import { Component } from '@angular/core';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Detection } from '../../../../models/detection';
import {
  selectCurrentDetection,
  selectDetectionEmergencyTemplate, selectDetectionShowById,
  selectSiteDetectionLabels
} from '../../../../features/detection/detection.selectors';
import {
  addDetectionEmergency,
  deleteDetection,
  loadDetectionReferenceAttachments,
  loadDetectionReferenceAttachmentsSuccess,
  selectDetection, setDetectionFavoriteAttachment,
  setDetectionShow,
  updateDetection,
} from '../../../../features/detection/detection.actions';
import { selectMeasure } from '../../../../features/measure/measure.actions';
import {
  selectVolume
} from '../../../../features/volume/volume.actions';
import { selectCut } from '../../../../features/cut/cut.actions';
import { CesiumTools } from '../../../../tools/cesium.tools';
import {
  setCameraConfiguration,
  setCameraMode
} from '../../../../features/cesium/cesium.actions';
import { CameraMode } from '../../../../enums/camera';
import { EmergencyTemplate } from '../../../../models/emergency-template';
import {
  combineLatest,
  distinctUntilChanged,
  first,
  tap, withLatestFrom
} from 'rxjs';
import { DetectionStatus } from '../../../../enums/detection';
import { FormControl, FormGroup } from '@angular/forms';
import { selectCurrentSite } from '../../../../features/site/site.selectors';
import { Site } from '../../../../models/site';
import { selectUser } from '../../../../features/user/user.selectors';
import { User } from '../../../../models/user';
import {
  DeleteDetectionDialog
} from '../../../dialogs/delete-detection-dialog/delete-detection.dialog';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import {
  selectTrackerElevation, selectTrackerImage
} from '../../../../features/tracker/tracker.actions';
import {
  setConfigExpandItemDetail,
} from '../../../../features/config/config.actions';
import {
  selectConfigDisplayDetections,
  selectConfigItemDetailExpanded
} from '../../../../features/config/config.selectors';
import { Attachment } from '../../../../models/attachment';
import {
  selectCurrentOrganizationId
} from '../../../../features/organization/organization.selectors';
import { Batch } from '../../../../models/batch';
import { SplitScreenMode } from '../marker-details/marker-details.component';
import {
  selectCurrentReferenceBatch
} from '../../../../features/batch/batch.selectors';
import { Actions, ofType } from '@ngrx/effects';
import { Color } from '../../../../enums/color';
import { SiteDetectionLabel } from '../../../../models/site-detection-label';



@Component({
    selector: 'detection-details',
    templateUrl: './detection-details.component.html',
    styleUrls: ['../details.component.scss', './detection-details.component.scss'],
    standalone: false
})
export class DetectionDetailsComponent {

  site: Site | null | undefined;
  detection: Detection | null | undefined;
  detectionStatusEnum = DetectionStatus;
  emergencyTemplate: EmergencyTemplate | undefined;
  siteDetectionLabels: SiteDetectionLabel[] | undefined;

  referenceBatch: Batch | null | undefined;

  selectedSplitScreenMode: SplitScreenMode = SplitScreenMode.SPLITTED;

  statusFormControl = new FormControl();
  typeFormControl = new FormControl();
  emeregencyFormControl = new FormControl();
  form = new FormGroup({
    status: this.statusFormControl,
    type: this.typeFormControl,
    emergency: this.emeregencyFormControl
  });
  formHasChanged: boolean = false;
  pictureUrl: string | undefined;
  overlayPolygonPoints?: string | null = null;

  attachments: Array<Attachment> = [];
  referenceAttachments: Attachment[] = [];

  private formBaseline: any | null = null;
  private formWatcherInitialized = false;

  readonly displayDetections$ = this.store.select(selectConfigDisplayDetections);
  readonly isExpanded$ = this.store.select(selectConfigItemDetailExpanded);
  readonly currentUser$ = this.store.select(selectUser);
  readonly currentOrganizationId$ = this.store.select(selectCurrentOrganizationId);


  constructor(private store: Store,
              private actions: Actions,
              private dialog: MatDialog,
              private translocoService: TranslocoService) {
    this.handleCurrentDetection();
    this.handleReferenceBatch();
    this.handleEmergencyTemplate();
    this.handleSiteDetectionLabels();
  }

  private handleReferenceBatch(): void {
    this.actions.pipe(ofType(loadDetectionReferenceAttachmentsSuccess), takeUntilDestroyed())
      .subscribe(data => {
        const polygonHexColor = this.detection?.textColor ?? Color.PRIMARY;
        this.referenceAttachments = (data.attachments ?? []).map(
          (attachment: Attachment) => ({
            ...attachment,
            polygonHexColor
          })
        );
      });

    combineLatest([
      this.store.select(selectCurrentDetection),
      this.store.select(selectCurrentReferenceBatch),
      this.store.select(selectCurrentSite)
    ])
      .pipe(
        distinctUntilChanged(
          (
            [previousDetection, previousBatch, previousSite],
            [currentDetection, currentBatch, currentSite]
          ) =>
            previousDetection?.id === currentDetection?.id &&
            previousBatch?.id === currentBatch?.id &&
            previousSite?.id === currentSite?.id &&
            previousSite?.organization === currentSite?.organization
        ),

        takeUntilDestroyed(),

        tap(([detection, referenceBatch, site]) => {
          this.referenceBatch = referenceBatch;

          if (!site || !detection || !referenceBatch) {
            this.referenceAttachments = [];
            return;
          }

          this.store.dispatch(
            loadDetectionReferenceAttachments({
              organizationId: site.organization,
              siteId: site.id,
              detectionId: detection.id,
              referenceBatchId: referenceBatch.id
            })
          );
        })
      )
      .subscribe();
  }

  private handleCurrentDetection(): void {
    if (!this.formWatcherInitialized) {
      this.formWatcherInitialized = true;

      this.form.valueChanges
        .pipe(takeUntilDestroyed())
        .subscribe((data: any) => {
          if (!this.detection || !this.formBaseline) {
            this.formHasChanged = false;
            return;
          }

          const statusChanged =
            data['status'] !== this.formBaseline['status'];

          const typeChanged =
            data['type'] !== this.formBaseline['type'];

          let emergencyChanged = false;

          if (data['status'] === DetectionStatus.CONFIRMED) {
            emergencyChanged =
              data['emergency'] !== this.formBaseline['emergency'];
          }

          this.formHasChanged =
            statusChanged ||
            typeChanged ||
            emergencyChanged;
        });
    }

    this.store.select(selectCurrentDetection)
      .pipe(
        withLatestFrom(
          this.store.select(selectCurrentSite)
        ),

        takeUntilDestroyed(),

        distinctUntilChanged(
          ([detectionA, siteA], [detectionB, siteB]) => {
            if (!detectionA && !detectionB) {
              return siteA?.id === siteB?.id;
            }

            if (!detectionA || !detectionB) {
              return false;
            }

            return (
              detectionA.id === detectionB.id &&
              detectionA.status === detectionB.status &&
              detectionA.label === detectionB.label &&
              detectionA.show === detectionB.show &&
              detectionA.favorite === detectionB.favorite &&
              (detectionA.emergency?.priority ?? null) ===
              (detectionB.emergency?.priority ?? null) &&
              siteA?.id === siteB?.id
            );
          }
        ),

        tap(([detection, site]) => {
          const previousDetection = this.detection;

          this.site = site ?? undefined;

          if (!detection) {
            this.detection = undefined;
            this.attachments = [];
            this.formBaseline = null;
            this.formHasChanged = false;
            return;
          }

          /*
           * Determine what actually changed before replacing
           * this.detection.
           */
          const detectionChanged =
            !previousDetection ||
            previousDetection.id !== detection.id;

          const statusChanged =
            !previousDetection ||
            previousDetection.status !== detection.status;

          const labelChanged =
            !previousDetection ||
            previousDetection.label !== detection.label;

          const emergencyChanged =
            !previousDetection ||
            (previousDetection.emergency?.priority ?? null) !==
            (detection.emergency?.priority ?? null);

          const favoriteChanged =
            !previousDetection ||
            previousDetection.favorite !== detection.favorite;

          /*
           * Always keep the local detection synchronized with NgRx.
           *
           * This is enough for a favorite-only change:
           *
           * detection.favorite
           *     ↓
           * template [favoriteFilename]
           *     ↓
           * carousel
           */
          this.detection = detection;

          /*
           * Favorite-only update.
           *
           * Do NOT rebuild attachments.
           * Do NOT reset the form.
           * Do NOT reset the other selected objects.
           *
           * The carousel receives the new favoriteFilename directly.
           */
          if (
            !detectionChanged &&
            !statusChanged &&
            !labelChanged &&
            !emergencyChanged
          ) {
            return;
          }

          /*
           * From here, something other than just the favorite changed.
           */
          this.overlayPolygonPoints = null;
          this.pictureUrl = undefined;

          /*
           * These selections only need to be reset when switching
           * to another detection.
           */
          if (detectionChanged) {
            this.store.dispatch(
              selectMeasure({ id: undefined })
            );

            this.store.dispatch(
              selectVolume({ id: undefined })
            );

            this.store.dispatch(
              selectCut({ id: undefined })
            );

            this.store.dispatch(
              selectTrackerImage({ id: undefined })
            );

            this.store.dispatch(
              selectTrackerElevation({ id: undefined })
            );
          }

          /*
           * Update form.
           */
          const emergencyPriority =
            this.detection.emergency
              ? this.detection.emergency.priority
              : 1;

          this.form.patchValue(
            {
              status: this.detection.status,
              type: this.detection.label,
              emergency: emergencyPriority.toString()
            },
            {
              emitEvent: false
            }
          );

          this.form.markAsPristine();

          this.form.updateValueAndValidity({
            emitEvent: false
          });

          this.formBaseline =
            this.form.getRawValue();

          this.formHasChanged = false;

          /*
           * Rebuild attachments only when necessary.
           *
           * In particular, a favorite-only update never reaches
           * this code.
           */
          this.attachments =
            structuredClone(this.detection.attachments) || [];

          this.attachments.forEach(
            (attachment: Attachment) => {
              attachment.polygonHexColor =
                this.detection?.textColor ?? Color.PRIMARY;
            }
          );
        })
      )
      .subscribe();
  }

  private handleEmergencyTemplate(): void {
    this.store.select(selectDetectionEmergencyTemplate)
      .pipe(takeUntilDestroyed())
      .subscribe((emergencyTemplate: EmergencyTemplate | undefined) => {
        this.emergencyTemplate = emergencyTemplate
      });
  }

  private handleSiteDetectionLabels(): void {
    this.store.select(selectSiteDetectionLabels)
      .pipe(takeUntilDestroyed())
      .subscribe((siteDetectionLabels: SiteDetectionLabel[] | undefined) => {
        this.siteDetectionLabels = siteDetectionLabels
      });
  }

  private updateDetection(site: Site, detection: Detection): void {
    this.store.dispatch(updateDetection({
      organizationId: site.organization,
      siteId: site.id,
      detectionId: detection.id,
      payload: {
        'status': this.statusFormControl.value,
        'label': this.typeFormControl.value,
      }
    }));
  }

  private updateEmergency(site: Site, user: User, detection: Detection): void {
    if (this.emergencyTemplate) {
      const etl = this.emergencyTemplate.labels
        .find(i =>  {
          return i.priority.toString() === this.emeregencyFormControl.value
        });
      if (etl) {
        this.store.dispatch(addDetectionEmergency({
          organizationId: site.organization,
          siteId: site.id,
          detectionId: detection.id,
          payload: {
            'creator': user.name,
            'emergency_level': etl.level,
            'emergency_label': etl.label,
            'emergency_priority': etl.priority,
            'emergency_color': etl.color
          }
        }));
      }
    }
  }

  onClickFlyTo(detection: Detection): void {
    const cameraConfiguration = CesiumTools.cameraConfigurationFromTargetAndDirection(detection.center, detection.cameraDirection, detection.length);
    if (!cameraConfiguration) return;
    this.store.dispatch(setCameraMode({cameraMode: CameraMode.MODE_3D}));
    this.store.dispatch(setCameraConfiguration({cameraConfiguration}));
  }

  onClickClose(): void {
    this.store.dispatch(setConfigExpandItemDetail({expand: false}));
    this.store.dispatch(selectDetection({id: undefined}));
  }

  onClickExpand(): void {
    this.store.dispatch(setConfigExpandItemDetail({expand: true}));
  }

  onClickCollapse(): void {
    this.store.dispatch(setConfigExpandItemDetail({expand: false}));
  }

  onClickToggleShow(detection: Detection): void {
    this.store.dispatch(setDetectionShow({id: detection.id, show: !detection.show}));
  }

  onClickUpdate(detection: Detection): void {
    combineLatest([
      this.store.select(selectCurrentSite),
      this.store.select(selectUser),
    ]).pipe(first())
      .subscribe(([site, user]) => {
        if (site) {
          this.updateDetection(site, detection);
          if (user
            && this.statusFormControl.value == DetectionStatus.CONFIRMED
            && (!detection.emergency || detection.emergency.priority != this.emeregencyFormControl.value)) {
            this.updateEmergency(site, user, detection);
          }
        }
      });
  }

  onClickDelete(detection: Detection): void {
    const deleteDialogRef = this.dialog.open(DeleteDetectionDialog, {
      disableClose: false
    });
    deleteDialogRef.componentInstance.message = this.translocoService.translate("would_you_like_to_delete", {name: detection.name});
    deleteDialogRef.afterClosed().subscribe(result => {
      if(result) {
        this.store.select(selectCurrentSite)
          .pipe(first())
          .subscribe((site: Site | null | undefined) => {
            if (site) {
              this.store.dispatch(selectDetection({id: undefined}));
              this.store.dispatch(deleteDetection({
                organizationId: site.organization,
                siteId: site.id,
                detectionId: detection.id,
                payload: {
                  'label': detection.label,
                  'status': result
                }}));
            }
          });
      }
    });
  }

  onSplitModeChanged(mode: SplitScreenMode): void {
    this.selectedSplitScreenMode = Number(mode);
  }

  onFavoriteChange(attachment: Attachment): void {
    if (!this.detection) {
      return;
    }

    combineLatest([
      this.currentOrganizationId$,
      this.store.select(selectCurrentSite),
    ])
      .pipe(first())
      .subscribe(([organizationId, site]) => {
        if (!organizationId || !site) {
          return;
        }

        this.store.dispatch(
          setDetectionFavoriteAttachment({
            organizationId,
            siteId: site.id,
            detectionId: this.detection!.id,
            filename: attachment.filename,
          })
        );
      });
  }

  dateFormat(user: User): string {
    return user.languageCode === 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy';
  }
}
